/* eslint-disable no-script-url */
import { html, LitElement, TemplateResult } from "lit";
import {
  customElement,
  property,
  queryAssignedNodes,
  state,
} from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { style } from "./assets/navigation-item.generated-style";
import { NavigationItemTarget } from "./navigation-item.constants";

/**
 * @CustomElement slh-navigation-item
 *
 * @cssprop --slh-default-font-family - Navigation font family.
 * @cssprop --slh-base-semi-bold-font-weight - Navigation font weight.
 * @cssprop --slh-base-m-font-size - Navigation font size.
 * @cssprop --slh-base-xxs-line-height - Navigation font line height.
 * @cssprop --slh-color-neutral-shade-40 - Readonly color.
 * @cssprop --slh-color-neutral-shade-60 - Color.
 * @cssprop --slh-color-neutral - Active color, hover color.
 * @cssprop --slh-color-primary - Focus color. Active border color.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 *  * ** slots
 * @slot default - Item label
 */
@customElement("slh-navigation-item")
export default class SlhNavigationItem extends LitElement {
  @queryAssignedNodes()
  protected defaultSlot: Array<HTMLElement> | undefined;

  /**
   * Active item
   */
  @property({
    type: Boolean,
  })
  active: boolean = false;

  /**
   * Readonly item
   */
  @property({
    type: Boolean,
  })
  readonly: boolean = false;

  /**
   * Navigation item link
   */
  @property({
    type: String,
  })
  link: string = "";

  /**
   * Navigation item target
   * it can be _blank, _self, _top or _parent
   */
  @property({
    type: NavigationItemTarget,
  })
  target: NavigationItemTarget = NavigationItemTarget.SELF;

  static styles = style;

  @state()
  private areaLabel = "";

  /**
   * Initially set a11y attributes based on textarea state
   */
  protected firstUpdated(): void {
    this.defaultSlot?.forEach((node: HTMLElement) => {
      const textContent = node.textContent?.trim();
      if (textContent) {
        this.areaLabel = textContent;
      }
    });
  }

  /**
   * Renders active item bottom border
   * @returns {TemplateResult}
   */
  private renderActiveBottomBorder(): TemplateResult {
    if (this.active) {
      return html`<div class="active-border"></div>`;
    }
    return html``;
  }

  render() {
    return html`<a
      class="slh-navigation-item ${classMap({
        active: this.active,
        readonly: this.readonly,
      })}"
      href="${!this.readonly ? this.link : "javascript:void(0)"}"
      tabindex="${!this.readonly && !this.active ? 1 : -1}"
      area-label="${this.areaLabel}"
      target="${this.target}"
      ><span> <slot></slot></span>
      ${this.renderActiveBottomBorder()}
    </a>`;
  }
}
